import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { MessageSquare, CalendarCheck, ChevronDown } from "lucide-react";
import Seo from "@/components/site/Seo";
import AcuityEmbed from "@/components/site/AcuityEmbed";
import Policies from "@/components/site/Policies";
import GalleryShowcase from "@/components/site/GalleryShowcase";
import { CONTACT, ACUITY } from "@/data/site";
import { beautySalonSchema, breadcrumb } from "@/data/schema";

export default function Book() {
  const [params] = useSearchParams();
  const category = params.get("category") || "";
  const appointmentType = params.get("appointmentType") || "";
  return (
    <>
      <Seo
        title="Book an Appointment | Golden Touch Lash & Brow, Vero Beach FL"
        description="Book your lash extensions, brow or beauty appointment with Golden Touch Lash & Brow in Vero Beach, Florida. Schedule online in just a few clicks."
        path="/book"
        jsonLd={[
          beautySalonSchema(),
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Book", path: "/book" },
          ]),
        ]}
      />

      <section className="bg-onyx pt-28 md:pt-36 pb-8 text-center">
        <div className="mx-auto max-w-3xl px-6">
          <p className="eyebrow">Reserve Your Appointment</p>
          <h1 className="mt-4 font-display text-4xl md:text-5xl text-ivory">Book With Golden Touch</h1>
          <p className="mx-auto mt-5 max-w-xl text-muted-ivory">
            Choose your service and preferred time below. Prefer to reach out directly? Text us
            anytime - we're happy to help.
          </p>

          {/* Primary booking action */}
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <a
              href="#scheduler"
              className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 text-[0.72rem] tracking-[0.22em] uppercase"
            >
              <CalendarCheck size={16} /> Book Appointment
            </a>
          </div>
          <div className="mt-3 flex flex-wrap justify-center gap-3">
            <a
              href={CONTACT.smsHref}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-[0.7rem] tracking-[0.2em] uppercase text-gold hover:text-gold-light transition-colors"
            >
              <MessageSquare size={15} /> Text Us {CONTACT.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* Bold policies - shown before booking so they are never overlooked */}
      <section className="bg-onyx pb-4">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10">
          <Policies compact />
        </div>
      </section>

      <section id="scheduler" className="bg-onyx pb-24 pt-8 scroll-mt-28">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-10">
          {category && (
            <div className="mb-4 flex flex-wrap items-center justify-center gap-3 text-center">
              <p className="text-sm text-ivory">
                Booking: <span className="font-semibold text-gold">{category}</span> - choose your
                service below.
              </p>
              <Link to="/book" className="text-xs uppercase tracking-[0.18em] text-gold/80 hover:text-gold">
                View all services
              </Link>
            </div>
          )}

          {/* Mobile-friendly scroll cue: makes it obvious more services are below */}
          <div className="mb-4 flex flex-col items-center gap-1 text-center sm:hidden" data-testid="scroll-cue">
            <p className="text-sm font-semibold text-ivory">
              Tap a service below to book
            </p>
            <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-gold">
              Scroll to see all services
              <ChevronDown size={15} className="animate-bounce" />
            </p>
          </div>

          <div className="border border-gold/20 bg-charcoal p-3 sm:p-5">
            <AcuityEmbed category={category} appointmentType={appointmentType} />
          </div>

          <p className="mt-4 text-center text-xs text-muted-ivory sm:hidden">
            More services continue below the list - keep scrolling to view every option.
          </p>
        </div>
      </section>

      {/* Work gallery, on the booking page */}
      <section className="bg-onyx pb-20" aria-label="Our work">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <div className="text-center">
            <p className="eyebrow">My Work</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl text-ivory">The Golden Touch</h2>
          </div>
          <div className="mt-8">
            <GalleryShowcase />
          </div>
        </div>
      </section>
    </>
  );
}
